import React, { useState, useEffect } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Button, IconButton, Flex,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure,
  FormControl, FormLabel, Input, Grid, TableContainer, Text, useToast, Badge,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon, ViewIcon, RepeatIcon } from '@chakra-ui/icons';
import { getInventory, createInventory, updateInventory, deleteInventory, getInventoryUsage, returnInventoryUsage } from '../Utilities/networks';
import PageShell from './ui/PageShell';
import DataCard from './ui/DataCard';

const Inventory = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();

  // For inventory usage modal
  const [isOpenUsage, setIsOpenUsage] = useState(false);
  const [usageData, setUsageData] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [returningUsageId, setReturningUsageId] = useState(null);

  const [deletingItem, setDeletingItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // form fields
  const [newMaterialName, setNewMaterialName] = useState('');
  const [newOverallStock, setNewOverallStock] = useState(0);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const res = await getInventory();
      if (res.data && res.data.data) {
        setItems(res.data.data);
      }
    } catch (e) {}
  };

  const handleAdd = () => {
    setEditingItem(null);
    setNewMaterialName('');
    setNewOverallStock(0);
    onOpen();
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setNewMaterialName(item.material_name);
    setNewOverallStock(item.overall_stock ?? 0);
    onOpen();
  };

  const handleSave = async () => {
    if (!newMaterialName) {
      onClose();
      return;
    }
    const stock = Number(newOverallStock);
    if (!Number.isFinite(stock) || stock <= 0) {
      toast({
        title: 'Stock cannot be zero or negative',
        description: 'Please enter a stock quantity greater than 0.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      if (editingItem) {
        await updateInventory({
          id: editingItem.id,
          material_name: newMaterialName,
          present_stock: stock,
          overall_stock: stock
        });
      } else {
        await createInventory({
          material_name: newMaterialName,
          present_stock: stock,
          overall_stock: stock
        });
      }
      fetchInventory();
    } catch (e) {
      toast({
        title: 'Failed to save material',
        description: e?.response?.data?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setEditingItem(null);
    onClose();
  };

  const handleDeleteClick = (item) => {
    setDeletingItem(item);
    onOpenDelete();
  };

  const confirmDelete = async () => {
    if (deletingItem) {
      try {
        await deleteInventory(deletingItem.id);
        fetchInventory();
      } catch (e) {}
    }
    setDeletingItem(null);
    onCloseDelete();
  };

  const loadUsageData = async (inventoryItem) => {
    const res = await getInventoryUsage(inventoryItem.id);
    if (res.data && res.data.data) {
      return res.data.data;
    }
    return [];
  };

  const handleViewUsage = async (inventoryItem) => {
    setSelectedInventory(inventoryItem);
    setUsageLoading(true);
    setUsageError('');
    setUsageData([]);
    setIsOpenUsage(true);
    try {
      setUsageData(await loadUsageData(inventoryItem));
    } catch (e) {
      setUsageError('Failed to fetch usage data');
    }
    setUsageLoading(false);
  };

  const handleReturnUsage = async (row) => {
    setReturningUsageId(row.usage_id);
    try {
      const res = await returnInventoryUsage(row.usage_id);
      if (res.data?.status === 'success') {
        toast({
          title: 'Inventory returned',
          description: res.data.message || 'Stock updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        if (selectedInventory) {
          setUsageData(await loadUsageData(selectedInventory));
        }
        fetchInventory();
      } else {
        toast({
          title: 'Return failed',
          description: res.data?.message || 'Could not return inventory.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (e) {
      toast({
        title: 'Return failed',
        description: e?.response?.data?.message || 'Could not return inventory.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    setReturningUsageId(null);
  };

  const filtered = items.filter(item => {
    const s = searchTerm.toLowerCase();
    return item.material_name.toLowerCase().includes(s);
  });

  return (
    <Box>
      <PageShell
        title="Inventory"
        subtitle="Track materials and stock used across service complaints"
        action={
          <Button leftIcon={<AddIcon />} colorScheme="brand" borderRadius="lg" onClick={handleAdd}>
            Add Material
          </Button>
        }
      >
        <FormControl maxW="320px" mb={0}>
          <Input
            placeholder="Search materials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRadius="lg"
            bg="white"
          />
        </FormControl>
      </PageShell>

      <DataCard>
        <TableContainer overflowX="auto">
          <Table variant="modern" size="sm">
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>Material</Th>
            <Th>Present Stock</Th>
            <Th>Overall Stock</Th>
            <Th textAlign="right">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filtered.map(item => (
            <Tr
              key={item.id}
              bg={item.present_stock !== item.overall_stock ? 'red.50' : undefined}
            >
              <Td fontWeight="600" color="slate.800">#{item.id}</Td>
              <Td fontWeight="500">{item.material_name}</Td>
              <Td>{item.present_stock}</Td>
              <Td>{item.overall_stock}</Td>
              <Td textAlign="right">
                <Flex justify="flex-end" gap={1}>
                {item.present_stock !== item.overall_stock && (
                  <IconButton
                    aria-label="View Usage"
                    icon={<ViewIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleViewUsage(item)}
                  />
                )}
                <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" variant="ghost" colorScheme="brand" onClick={() => handleEdit(item)} />
                <IconButton aria-label="Delete" icon={<DeleteIcon />} size="sm" variant="ghost" colorScheme="red" onClick={() => handleDeleteClick(item)} />
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
        </TableContainer>
        {filtered.length === 0 && (
          <Box py={12} textAlign="center"><Text color="slate.500" fontSize="sm">No materials found.</Text></Box>
        )}
      </DataCard>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader fontFamily="heading">{editingItem ? 'Edit Material' : 'Add Material'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Grid templateColumns="1fr" gap={6}>
              <FormControl>
                <FormLabel>Material Name</FormLabel>
                <Input value={newMaterialName} onChange={e => setNewMaterialName(e.target.value)} placeholder="e.g. Hammer" />
              </FormControl>
              <FormControl>
                <FormLabel>Overall Stock</FormLabel>
                <Input type="number" min={1} value={newOverallStock} onChange={e => setNewOverallStock(e.target.value)} placeholder="e.g. 200" />
              </FormControl>
            </Grid>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button colorScheme="teal" mx={3} onClick={handleSave}>Save</Button>
            <Button variant="ghost" mx={3} onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpenDelete} onClose={() => { onCloseDelete(); setDeletingItem(null); }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign="center">
            {deletingItem && (
              <Box>Are you sure you want to delete <strong>{deletingItem.material_name}</strong>?</Box>
            )}
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button colorScheme="red" mx={3} onClick={confirmDelete}>Delete</Button>
            <Button variant="ghost" mx={3} onClick={() => { onCloseDelete(); setDeletingItem(null); }}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Inventory Usage Modal */}
      <Modal isOpen={isOpenUsage} onClose={() => setIsOpenUsage(false)} size={"xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">
            Inventory Usage
            {selectedInventory && (
              <Box fontSize="sm" color="gray.500" mt={1}>
                {selectedInventory.material_name}
              </Box>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {usageLoading ? (
              <Box textAlign="center" py={6}>Loading...</Box>
            ) : usageError ? (
              <Box color="red.500" textAlign="center" py={6}>{usageError}</Box>
            ) : usageData.length === 0 ? (
              <Box textAlign="center" py={6}>No complaints found for this inventory.</Box>
            ) : (
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Complaint ID</Th>
                    <Th>Technician Name</Th>
                    <Th>Technician Phone</Th>
                    <Th>Quantity</Th>
                    <Th>Status</Th>
                    <Th textAlign="right">Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {usageData.map((row) => {
                    const isReturned = row.returned === true || row.returned === 'true';
                    return (
                    <Tr key={row.usage_id}>
                      <Td>#{row.complaint_id}</Td>
                      <Td>{row.technician_name || '-'}</Td>
                      <Td>{row.phone || '-'}</Td>
                      <Td>{row.quantity ?? '-'}</Td>
                      <Td>
                        <Badge colorScheme={isReturned ? 'green' : 'red'} borderRadius="md" px={2}>
                          {isReturned ? 'Returned' : 'Not returned'}
                        </Badge>
                      </Td>
                      <Td textAlign="right">
                        {!isReturned && (
                          <IconButton
                            aria-label="Return to stock"
                            icon={<RepeatIcon />}
                            size="sm"
                            colorScheme="teal"
                            variant="ghost"
                            isLoading={returningUsageId === row.usage_id}
                            onClick={() => handleReturnUsage(row)}
                          />
                        )}
                      </Td>
                    </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            )}
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button variant="ghost" onClick={() => setIsOpenUsage(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Inventory;
